import { useEffect, useRef } from "react";
import Swal from "sweetalert2";
import * as UserService from "../Services/User/UserService";
import { SESSION_IDLE_MINUTES } from "./CommonVariables";
import { endSession, logoutUser } from "./SessionManager";

const ACTIVITY_KEY = "pp_last_activity";
const WARNING_SECONDS = 60;
const KEEP_ALIVE_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 5000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

const readLastActivity = () => Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now();

const SessionIdleTimer = () => {
  const lastWriteRef = useRef(0);
  const lastKeepAliveRef = useRef(Date.now());
  const warningOpenRef = useRef(false);

  useEffect(() => {
    const idleMs = SESSION_IDLE_MINUTES * 60 * 1000;
    const warningMs = WARNING_SECONDS * 1000;
    let ended = false;

    const writeActivity = () => {
      lastWriteRef.current = Date.now();
      localStorage.setItem(ACTIVITY_KEY, String(lastWriteRef.current));
    };

    const markActivity = () => {
      if (!warningOpenRef.current && Date.now() - lastWriteRef.current > ACTIVITY_WRITE_THROTTLE_MS) {
        writeActivity();
      }
    };

    const keepAlive = () => {
      lastKeepAliveRef.current = Date.now();
      UserService.KeepAlive().catch(() => {});
    };

    const showWarning = (remainingMs) => {
      warningOpenRef.current = true;
      Swal.fire({
        icon: "warning",
        title: "Session about to expire",
        text: "You will be logged out soon due to inactivity.",
        timer: remainingMs,
        timerProgressBar: true,
        showCancelButton: true,
        confirmButtonText: "Stay signed in",
        cancelButtonText: "Log out",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        warningOpenRef.current = false;
        if (result.isConfirmed) {
          writeActivity();
          keepAlive();
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          ended = true;
          logoutUser();
        }
      });
    };

    const check = () => {
      if (ended) {
        return;
      }

      const idleFor = Date.now() - readLastActivity();

      if (idleFor >= idleMs) {
        ended = true;
        endSession();
      } else if (idleFor >= idleMs - warningMs) {
        if (!warningOpenRef.current) {
          showWarning(idleMs - idleFor);
        }
      } else {
        if (warningOpenRef.current) {
          warningOpenRef.current = false;
          Swal.close();
        }
        if (Date.now() - lastKeepAliveRef.current > KEEP_ALIVE_INTERVAL_MS && idleFor < KEEP_ALIVE_INTERVAL_MS) {
          keepAlive();
        }
      }
    };

    writeActivity();
    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));
    const intervalId = setInterval(check, 1000);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, markActivity));
    };
  }, []);

  return null;
};

export default SessionIdleTimer;
